import { array, boolean, mixed, number, object, string } from 'yup';
import { format } from 'date-fns';
import { tableSortFilter } from '@/utils/arrayProcessing';
import { fNumber } from '@/utils/formatNumber';
import { clientManagementPath } from '@/features/users/clients/routes/paths';
import { providersPath } from '@/features/users/providers/routes/paths';
import {
  InAppPopupActionUser,
  InAppPopup,
  InAppPopupButton,
  InAppPopupDto,
  InAppPopupFilters,
  InAppPopupFormButton,
  InAppPopupFormValues,
  InAppPopupPlatform,
  InAppPopupStats,
  InAppPopupUpdateDto,
} from '../types';

export const IN_APP_POPUP_PERMISSION_MODULE = 'settings';

export const IN_APP_POPUP_PLATFORM_OPTIONS: {
  value: InAppPopupPlatform;
  label: string;
}[] = [
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
];

export const IN_APP_POPUP_BUTTON_STYLE_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
];

export const IN_APP_POPUP_ACTION_TYPE_OPTIONS = [
  { value: 'dismiss', label: 'Dismiss' },
  { value: 'route', label: 'App route' },
  { value: 'external_url', label: 'External URL' },
];

export const IN_APP_POPUP_ROUTE_OPTIONS = [
  { value: '/booking-location', label: '/booking-location' },
];

export const IN_APP_POPUPS_TABLE_HEAD = [
  { id: 'id', label: 'ID', minWidth: 130 },
  { id: 'title', label: 'Title', minWidth: 160 },
  { id: 'platforms', label: 'Platforms', minWidth: 110 },
  { id: 'priority', label: 'Priority', minWidth: 72 },
  { id: 'starts_at', label: 'Starts', minWidth: 125 },
  { id: 'expires_at', label: 'Expires', minWidth: 125 },
  { id: 'buttons', label: 'Buttons', minWidth: 130 },
  { id: 'stats', label: 'Stats', minWidth: 210 },
  { id: 'guest_eligible', label: 'Guests', minWidth: 70 },
  { id: 'active', label: 'Status', minWidth: 80 },
  { id: '3-dot-menu', label: '', width: 64 },
];

export const createDefaultPopupButton = (): InAppPopupFormButton => ({
  label: 'Maybe later',
  style: 'secondary',
  action_type: 'dismiss',
  route: '/booking-location',
  external_url: '',
});

export const inAppPopupDefaultValues: InAppPopupFormValues = {
  id: '',
  title: '',
  body: '',
  banner_image_url: '',
  image_alt: '',
  starts_at: '',
  expires_at: '',
  active: true,
  guest_eligible: false,
  platforms: [],
  priority: 0,
  buttons: [createDefaultPopupButton()],
};

const isValidDateValue = (value?: string | null) => {
  if (!value) return false;
  return !Number.isNaN(new Date(value).getTime());
};

export const isHttpUrl = (value?: string | null) => {
  if (!value) return false;

  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const platformValues = IN_APP_POPUP_PLATFORM_OPTIONS.map(
  (option) => option.value
);
const buttonStyleValues = IN_APP_POPUP_BUTTON_STYLE_OPTIONS.map(
  (option) => option.value
);
const actionTypeValues = IN_APP_POPUP_ACTION_TYPE_OPTIONS.map(
  (option) => option.value
);
const allowedRoutes = IN_APP_POPUP_ROUTE_OPTIONS.map((option) => option.value);

const PopupButtonSchema = object()
  .shape({
    label: string().trim().required('Button label is required'),
    style: mixed()
      .oneOf(buttonStyleValues, 'Button style must be primary or secondary')
      .required('Button style is required'),
    action_type: mixed()
      .oneOf(
        actionTypeValues,
        'Button action must be dismiss, route, or external URL'
      )
      .required('Button action is required'),
    route: string(),
    external_url: string(),
  })
  .test('valid-action-target', function (button) {
    if (!button) return true;

    const actionType = button.action_type;

    if (actionType === 'route' && !allowedRoutes.includes(button.route ?? '')) {
      return this.createError({
        path: `${this.path}.route`,
        message: 'Route must be allowlisted',
      });
    }

    if (
      actionType === 'external_url' &&
      !isHttpUrl(button.external_url ?? '')
    ) {
      return this.createError({
        path: `${this.path}.external_url`,
        message: 'External URL must start with http or https',
      });
    }

    return true;
  });

export const InAppPopupSchema = object().shape({
  id: string()
    .trim()
    .matches(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      'Use lowercase letters, numbers, and hyphens'
    )
    .required('ID is required'),
  title: string().trim().required('Title is required'),
  body: string().trim().required('Body is required'),
  banner_image_url: string()
    .trim()
    .test(
      'optional-http-url',
      'Banner URL must start with http or https',
      (value) => (value ? isHttpUrl(value) : true)
    ),
  image_alt: string().trim(),
  starts_at: string()
    .required('Start date is required')
    .test('valid-start-date', 'Start date is invalid', isValidDateValue),
  expires_at: string()
    .required('Expiry date is required')
    .test('valid-expiry-date', 'Expiry date is invalid', isValidDateValue)
    .test(
      'expires-after-starts',
      'Expiry date must be after start date',
      function (value) {
        const { starts_at } = this.parent as InAppPopupFormValues;
        if (!value || !starts_at) return true;
        return new Date(value).getTime() > new Date(starts_at).getTime();
      }
    ),
  active: boolean().required(),
  guest_eligible: boolean().required(),
  platforms: array()
    .of(mixed().oneOf(platformValues, 'Platform must be iOS or Android'))
    .default([]),
  priority: number()
    .typeError('Priority must be a number')
    .integer('Priority must be an integer')
    .required('Priority is required'),
  buttons: array()
    .of(PopupButtonSchema)
    .max(2, 'Popups support up to 2 buttons')
    .default([]),
});

export function toDateTimeLocalInputValue(value?: string | Date | null) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return format(date, "yyyy-MM-dd'T'HH:mm");
}

const trimToNullable = (value?: string | null) => {
  const trimmedValue = value?.trim();
  return trimmedValue ? trimmedValue : null;
};

const toPopupButtonPayload = (
  button: InAppPopupFormButton
): InAppPopupButton => {
  if (button.action_type === 'route') {
    return {
      label: button.label.trim(),
      style: button.style,
      action: {
        type: 'route',
        route: button.route,
      },
    };
  }

  if (button.action_type === 'external_url') {
    return {
      label: button.label.trim(),
      style: button.style,
      action: {
        type: 'external_url',
        external_url: button.external_url.trim(),
      },
    };
  }

  return {
    label: button.label.trim(),
    style: button.style,
    action: {
      type: 'dismiss',
    },
  };
};

export function inAppPopupFormToPayload(
  values: InAppPopupFormValues
): InAppPopupDto {
  return {
    id: values.id.trim(),
    title: values.title.trim(),
    body: values.body.trim(),
    banner_image_url: trimToNullable(values.banner_image_url),
    image_alt: trimToNullable(values.image_alt),
    starts_at: new Date(values.starts_at).toISOString(),
    expires_at: new Date(values.expires_at).toISOString(),
    active: values.active,
    guest_eligible: values.guest_eligible,
    platforms: values.platforms,
    priority: Number(values.priority),
    buttons: values.buttons.map(toPopupButtonPayload),
  };
}

export function inAppPopupPayloadForUpdate(
  values: InAppPopupFormValues
): InAppPopupUpdateDto {
  const { id, ...payload } = inAppPopupFormToPayload(values);
  return payload;
}

const actionToFormValues = (button: InAppPopupButton): InAppPopupFormButton => {
  if (button.action.type === 'route') {
    return {
      label: button.label,
      style: button.style,
      action_type: 'route',
      route: button.action.route,
      external_url: '',
    };
  }

  if (button.action.type === 'external_url') {
    return {
      label: button.label,
      style: button.style,
      action_type: 'external_url',
      route: '/booking-location',
      external_url: button.action.external_url,
    };
  }

  return {
    label: button.label,
    style: button.style,
    action_type: 'dismiss',
    route: '/booking-location',
    external_url: '',
  };
};

export function inAppPopupToFormValues(
  popup: InAppPopup
): InAppPopupFormValues {
  return {
    id: popup.id,
    title: popup.title,
    body: popup.body,
    banner_image_url: popup.banner_image_url ?? '',
    image_alt: popup.image_alt ?? '',
    starts_at: toDateTimeLocalInputValue(popup.starts_at),
    expires_at: toDateTimeLocalInputValue(popup.expires_at),
    active: popup.active,
    guest_eligible: popup.guest_eligible ?? false,
    platforms: popup.platforms ?? [],
    priority: popup.priority,
    buttons: popup.buttons?.length
      ? popup.buttons.map(actionToFormValues)
      : [createDefaultPopupButton()],
  };
}

export function buildDuplicatePopupId(id: string) {
  const cleanId = id
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `${cleanId || 'popup'}-copy`;
}

export function inAppPopupToDuplicateFormValues(
  popup: InAppPopup
): InAppPopupFormValues {
  return {
    ...inAppPopupToFormValues(popup),
    id: buildDuplicatePopupId(popup.id),
  };
}

export function getPopupActionLabel(button: InAppPopupButton) {
  if (button.action.type === 'route') {
    return `Route: ${button.action.route}`;
  }

  if (button.action.type === 'external_url') {
    return `URL: ${button.action.external_url}`;
  }

  return 'Dismiss';
}

export function getPopupPlatformLabel(platform: InAppPopupPlatform) {
  return (
    IN_APP_POPUP_PLATFORM_OPTIONS.find((option) => option.value === platform)
      ?.label ?? platform
  );
}

export function formatInAppPopupStatsCount(value?: number | null) {
  if (value === undefined || value === null) return '-';
  return fNumber(value);
}

export function getInAppPopupTapRate(stats?: InAppPopupStats | null) {
  if (!stats) return null;
  if (!stats.impressions) return 0;
  return (stats.button_taps / stats.impressions) * 100;
}

export function formatInAppPopupTapRate(stats?: InAppPopupStats | null) {
  const tapRate = getInAppPopupTapRate(stats);

  if (tapRate === null) return '-';

  return `${tapRate % 1 === 0 ? tapRate.toFixed(0) : tapRate.toFixed(1)}%`;
}

export function formatInAppPopupActionEventType(eventType?: string | null) {
  if (eventType === 'dismiss') return 'Dismissed';
  if (eventType === 'button_tap') return 'Button tap';
  return eventType ? eventType.replace(/_/g, ' ') : 'Action';
}

export function getInAppPopupActionUserDisplayName(
  user?: Partial<InAppPopupActionUser> | null
) {
  if (!user) return 'Unknown user';

  const fullName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (fullName) return fullName;
  if (user.email) return user.email;
  if (user.user_id) return `User ${user.user_id}`;

  return 'Unknown user';
}

export function getInAppPopupActionUserPath(
  user?: Partial<InAppPopupActionUser> | null
) {
  if (!user?.user_id) return null;

  if (Number(user.user_type) === 1) {
    return clientManagementPath('edit', {
      userId: String(user.user_id),
      tabSid: 'details',
    });
  }

  if (Number(user.user_type) === 2) {
    return providersPath('edit', {
      userId: String(user.user_id),
      tabSid: 'details',
    });
  }

  return null;
}

export function applySortFilterInAppPopups({
  tableData,
  comparator,
  filters,
  filterStatus,
}: {
  tableData: InAppPopup[];
  comparator: (a: InAppPopup, b: InAppPopup) => number;
  filters: InAppPopupFilters;
  filterStatus: string;
}) {
  let filteredData: InAppPopup[] = tableSortFilter(tableData, comparator);
  const query = filters.query.trim().toLowerCase();

  if (query) {
    filteredData = filteredData.filter((popup) => {
      const buttonText = popup.buttons
        ?.map(
          (button: InAppPopupButton) =>
            `${button.label} ${getPopupActionLabel(button)}`
        )
        .join(' ')
        .toLowerCase();

      return [
        popup.id,
        popup.title,
        popup.body,
        String(popup.priority),
        popup.guest_eligible ? 'guest eligible guests' : 'registered users',
        ...(popup.platforms ?? []),
        buttonText,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query));
    });
  }

  if (filterStatus === 'active') {
    filteredData = filteredData.filter((popup) => popup.active);
  }

  if (filterStatus === 'inactive') {
    filteredData = filteredData.filter((popup) => !popup.active);
  }

  const platformFilter = filters.platform;

  if (platformFilter) {
    filteredData = filteredData.filter(
      (popup) =>
        !popup.platforms?.length || popup.platforms.includes(platformFilter)
    );
  }

  return filteredData;
}
